import { PutObjectCommand } from '@aws-sdk/client-s3'
import * as Sentry from '@sentry/react'
import { UPLOAD_BUCKET, withS3Client, type S3Io } from './s3'
import { getCurrentIdentityId } from '../auth/currentIdentity'

// Re-export so existing call sites keep working.
export { getIdentityId, UPLOAD_BUCKET } from './s3'

// Telemetry-only threshold, not a limit: uploads this large mean our
// assumptions about content size are off, so flag them in Sentry. The
// upload itself always proceeds.
export const UPLOAD_WARN_SIZE_BYTES = 100 * 1024 * 1024

export type UploadFile = {
  name: string
  blob: Blob
}

export type UploadProgress = {
  completed: number
  total: number
  currentName: string
}

export type UploadFilesOptions = S3Io & {
  files: UploadFile[]
  prefix: string
  // The identity whose folder these files live under. Defaults to the active
  // identity, but callers running a long, multi-step operation should pass it
  // explicitly so a mid-flight impersonation change can't redirect the writes.
  identityId?: string
  onProgress?: (progress: UploadProgress) => void
}

export async function uploadFiles({
  files,
  prefix,
  credentials,
  region,
  identityId = getCurrentIdentityId(),
  onProgress,
}: UploadFilesOptions): Promise<void> {
  await withS3Client({ credentials, region }, async (client) => {
    for (let i = 0; i < files.length; i++) {
      const { name, blob } = files[i]
      onProgress?.({ completed: i, total: files.length, currentName: name })
      if (blob.size > UPLOAD_WARN_SIZE_BYTES) {
        Sentry.captureMessage('upload exceeds 100 MB', {
          level: 'warning',
          extra: { name, sizeBytes: blob.size, prefix, identityId },
        })
      }
      const body = new Uint8Array(await blob.arrayBuffer())
      await client.send(
        new PutObjectCommand({
          Bucket: UPLOAD_BUCKET,
          Key: `${identityId}/${prefix}/${name}`,
          Body: body,
          ContentType: blob.type || 'application/octet-stream',
        }),
      )
    }
    onProgress?.({ completed: files.length, total: files.length, currentName: '' })
  })
}
